import { detectInAppBrowser, buildWayOut } from './in-app-browser.js';

/**
 * PLAY FREE LEAVES THE APP'S OWN BROWSER (17 October 2026, the paid ads).
 *
 * An ad on Instagram, Facebook or TikTok opens moonfall.io inside the app's
 * own browser. The landing works there, upright; the game does not: on a phone
 * it is landscape-only, and an app's browser is locked upright. Play Free used
 * to take that visitor to /play inside the app, where the first thing they met
 * was the rotate screen's card, "The Instagram browser isn't supported."
 *
 * Inside a named app's browser on a phone, every Play Free link is now that
 * app's own way out (in-app-browser.js `buildWayOut`), aimed at /play with
 * exactly the tags landing-utm.js gave the link: the visitor's tap opens their
 * real browser on the game, with the ad's attribution.
 *
 * A REAL TAP ON A REAL LINK. The apps honour these links from a tap and drop
 * them from a script (eiab: a redirect hangs Facebook 555), so the link's own
 * address is the way out and the tap the visitor makes is the one the app
 * answers, as on the /play card. Facebook on iPhone passes the Safari scheme
 * on only from a new window, so its tap opens one, exactly as there.
 *
 * WHERE NO WAY OUT IS KNOWN (TikTok, Snapchat, WeChat) nothing changes: Play
 * Free goes to /play, whose card offers Copy link and the app's own menu.
 *
 * A TAP THAT WENT NOWHERE. None of the links is documented by the app that
 * honours it, and an app's ad browser can ignore one (in-app-browser.js). A
 * tap that worked takes the page off screen: it is hidden, or its timers stand
 * still. If the page is still on screen FALLBACK_AFTER_MS after a tap, Play
 * Free goes where it always went, /play in this browser, as a press of a /play
 * link (the landing's runway yields to it as to any other). It tells /play the
 * way out was tried first (sessionStorage `moonfall_way_out_tried`, this tab
 * only), so the card there opens on the next step instead of the same button.
 *
 * STILL A PLAY PRESS. The funnel beacon counts a press of a /play link, and
 * these no longer look like one, so each tap is counted here
 * (`landing_play_click`, the same step, still one a page view).
 *
 * WHEN. This runs in the landing's first bundle, after landing-utm.js has
 * decorated the links and before /env.js. A link that arrives later is armed
 * on DOMContentLoaded, after landing-utm.js decorates it on the same event.
 *
 * AN ORDINARY BROWSER GETS NOTHING: no link changed, no listener, no global.
 * Contract: tests/unit/client/landingWayOut.test.js
 */
(function () {
  'use strict';

  /* A second evaluation (a test re-requiring the file) would leave the first
     one's links and listeners behind. */
  try {
    if (window.MoonfallLandingWayOut && typeof window.MoonfallLandingWayOut.destroy === 'function') {
      window.MoonfallLandingWayOut.destroy();
    }
  } catch (_) { /* nothing to retire */ }

  let found = null;
  try {
    found = detectInAppBrowser(window.navigator.userAgent);
  } catch (_) {
    found = null;
  }
  if (!found) return;

  /* How long after a tap the visitor is taken to be still here. Shorter than
     the /play card's own wait: a visitor who pressed Play Free and saw nothing
     happen should be on their way within a breath, and a link that works has
     taken the page off screen well inside this. */
  const FALLBACK_AFTER_MS = 1500;
  /* A timer this much later than it was set for did not run late: the app was
     off screen, where timers stand still (the /play card's own rule). */
  const AWAY_AFTER_MS = 5000;
  /* Read once by in-app-browser-flag.js on /play, then removed. */
  const TRIED_KEY = 'moonfall_way_out_tried';
  const PLAY_STEP = 'landing_play_click';
  /* Exactly /play, optionally with a query or a fragment: landing-utm.js's and
     the beacon's own test. */
  const PLAY_LINK = /^\/play(?:[?#]|$)/;

  /** @type {Array<{ link: Element, play: string, way: { href: string, open: string }, onClick: (event: Event) => void }>} */
  const armed = [];
  let timer = null;
  let tappedAt = 0;
  let leftSinceTap = false;
  let waitingFor = '';

  function onScreen() {
    try {
      return document.visibilityState === 'visible';
    } catch (_) {
      return true;
    }
  }

  function count() {
    try {
      if (window.MoonfallFunnel && typeof window.MoonfallFunnel.track === 'function') {
        window.MoonfallFunnel.track(PLAY_STEP);
      }
    } catch (_) { /* a count is best-effort */ }
  }

  /* Facebook on iPhone passes the Safari scheme on only from a new window
     opened by the tap (in-app-browser.js). The link keeps its address, so if
     the window cannot even be asked, the link goes by itself. */
  function openInWindow(event, href) {
    try {
      window.open(href, '_blank');
    } catch (_) {
      return;
    }
    event.preventDefault();
  }

  /* Play Free as it always was: a press of a /play link, in this browser. A
     link of its own, put in the page for the press and taken out after it, so
     the landing's runway sees a /play press and yields to it. */
  function goToPlay(play) {
    try {
      window.sessionStorage.setItem(TRIED_KEY, '1');
    } catch (_) { /* the card then simply opens on its button */ }
    const press = document.createElement('a');
    press.setAttribute('href', play);
    press.hidden = true;
    (document.body || document.documentElement).appendChild(press);
    try {
      press.click();
    } finally {
      press.remove();
    }
  }

  function stillHere() {
    timer = null;
    const late = Date.now() - tappedAt - FALLBACK_AFTER_MS;
    if (leftSinceTap || !onScreen() || late > AWAY_AFTER_MS) return;
    goToPlay(waitingFor);
  }

  /* A second tap while the first is waited on neither hurries the fallback
     nor puts it off; the link itself goes again every time. */
  function noteTap(play) {
    if (timer !== null) return;
    leftSinceTap = false;
    tappedAt = Date.now();
    waitingFor = play;
    timer = setTimeout(stillHere, FALLBACK_AFTER_MS);
  }

  function noteLeaving() {
    if (!onScreen()) leftSinceTap = true;
  }

  function arm() {
    document.querySelectorAll('a[href]').forEach((link) => {
      if (armed.some((entry) => entry.link === link)) return;
      const play = link.getAttribute('href') || '';
      if (!PLAY_LINK.test(play)) return;
      let address = '';
      try {
        address = new URL(play, window.location.href).href;
      } catch (_) {
        return;
      }
      const way = buildWayOut(found, address);
      if (!way) return;
      const entry = {
        link,
        play,
        way,
        /** @param {Event} event */
        onClick(event) {
          // A press something else on the page cancelled goes nowhere.
          if (event.defaultPrevented) return;
          count();
          if (way.open === 'window') openInWindow(event, way.href);
          noteTap(play);
        },
      };
      link.setAttribute('href', way.href);
      link.addEventListener('click', entry.onClick);
      armed.push(entry);
    });
  }

  function lastLook() {
    arm();
  }

  arm();
  document.addEventListener('visibilitychange', noteLeaving);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', lastLook, { once: true });
  }

  function destroy() {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    document.removeEventListener('visibilitychange', noteLeaving);
    document.removeEventListener('DOMContentLoaded', lastLook);
    armed.splice(0).forEach((entry) => {
      entry.link.removeEventListener('click', entry.onClick);
      if (entry.link.getAttribute('href') === entry.way.href) entry.link.setAttribute('href', entry.play);
    });
    try {
      delete window.MoonfallLandingWayOut;
    } catch (_) { /* a frozen window keeps it; it is inert now */ }
  }

  /* Read by the tests. Frozen so a page cannot rewrite the answer. */
  window.MoonfallLandingWayOut = Object.freeze({
    app: found.app,
    os: found.os,
    FALLBACK_AFTER_MS: FALLBACK_AFTER_MS,
    AWAY_AFTER_MS: AWAY_AFTER_MS,
    destroy: destroy,
  });
}());
